import { parseValue } from "../fields/InputField";
import { callerField, fieldAttrName } from "../../../utils/caller";

export const isCustomField = field => {
  return /^customField\d+$/.test(field.id);
};

export const isCostCenterField = field => {
  return field.id === "idCostCenter" || field.attrName === "costCenterId";
};

export const getCustomFieldTemplateId = fieldId => {
  return fieldId.replace("customField", "");
};

export const findExistingCustomField = (entity, customFieldTemplateId) => {
  return entity?.customFields?.find(
    cf => String(cf.customFieldTemplate?.id) === String(customFieldTemplateId)
  );
};

const persistedCustomFieldId = customField => {
  if (!customField?.id) return null;
  if (customField.id.toString().startsWith("temp_")) return null;

  return customField.id;
};

const customFieldData = (customFieldTemplateId, value, existingCustomField) => {
  const existingId = persistedCustomFieldId(existingCustomField);
  const fieldValue = value?.toString() ?? "";

  return existingId
    ? {
        id: existingId,
        value: fieldValue,
      }
    : {
        custom_field_template_id: parseInt(customFieldTemplateId),
        value: fieldValue,
      };
};

const customFieldDataFor = (entity, customFieldTemplateId, value) => {
  return customFieldData(
    customFieldTemplateId,
    value,
    findExistingCustomField(entity, customFieldTemplateId)
  );
};

const customFieldIdentity = customField => {
  if (customField.id) return `id:${customField.id}`;
  return `template:${customField.custom_field_template_id || customField.customFieldTemplateId}`;
};

const mergeCustomFields = customFields => {
  const byIdentity = new Map();
  customFields.forEach(customField => {
    byIdentity.set(customFieldIdentity(customField), customField);
  });

  return Array.from(byIdentity.values());
};

const withCustomFieldValues = (entity, customFields) => {
  const updatedCustomFields = entity.customFields ? [...entity.customFields] : [];

  customFields.forEach(customField => {
    const existingId = persistedCustomFieldId(customField);
    if (!existingId) return;

    const index = updatedCustomFields.findIndex(existing => existing.id === existingId);
    if (index === -1) return;

    updatedCustomFields[index] = {
      ...updatedCustomFields[index],
      value: customField.value,
    };
  });

  return updatedCustomFields;
};

const getCostCenterId = asset => {
  const hasCostCenter = Object.prototype.hasOwnProperty.call(asset || {}, "costCenter");
  const id = asset?.costCenter?.id ?? asset?.costCenter?.costCenterId ?? asset?.costCenterId;

  return id === undefined && hasCostCenter ? null : id;
};

export const createCustomFieldPayload = (customFieldTemplateId, value, existingCustomField) => {
  return {
    customFields: [customFieldData(customFieldTemplateId, value, existingCustomField)],
  };
};

export const normalizeAssetUpdatePayload = (payload, entity) => {
  const normalizedPayload = { ...payload };
  const customFields = Array.isArray(normalizedPayload.customFields)
    ? [...normalizedPayload.customFields]
    : [];

  Object.keys(normalizedPayload).forEach(key => {
    const match = key.match(/^customField(\d+)$/);
    if (!match) return;

    const customFieldTemplateId = match[1];
    customFields.push(customFieldDataFor(entity, customFieldTemplateId, normalizedPayload[key]));
    delete normalizedPayload[key];
  });

  if (customFields.length > 0) {
    normalizedPayload.customFields = mergeCustomFields(customFields);
  }

  return normalizedPayload;
};

export const createCustomFieldLocalStateUpdate = (
  entity,
  customFieldTemplateId,
  value,
  existingCustomField
) => {
  const updatedCustomFields = entity.customFields ? [...entity.customFields] : [];

  if (existingCustomField) {
    const index = updatedCustomFields.findIndex(cf => cf.id === existingCustomField.id);
    if (index !== -1) {
      updatedCustomFields[index] = {
        ...updatedCustomFields[index],
        value: value?.toString() ?? "",
      };
    }
  } else {
    updatedCustomFields.push({
      id: `temp_${customFieldTemplateId}`,
      value: value?.toString() ?? "",
      customFieldTemplate: {
        id: customFieldTemplateId,
      },
    });
  }

  return {
    customFields: updatedCustomFields,
    [`customField${customFieldTemplateId}`]: value?.toString() ?? "",
  };
};

export const handleCustomFieldUpdate = (entity, field, newValue, onChange, options = {}) => {
  const customFieldTemplateId = getCustomFieldTemplateId(field.id);
  const existingCustomField = findExistingCustomField(entity, customFieldTemplateId);

  const customFieldPayload = createCustomFieldPayload(
    customFieldTemplateId,
    newValue,
    existingCustomField
  );
  const localStateUpdate = createCustomFieldLocalStateUpdate(
    entity,
    customFieldTemplateId,
    newValue,
    existingCustomField
  );

  onChange(entity.id, customFieldPayload, { ...options, localStateUpdate });
};

export const processFieldsForBulkOperation = (fields, entity) => {
  const customFields = [];
  const regularFields = {};
  const localStateUpdates = {};

  fields.forEach(field => {
    if (isCustomField(field)) {
      const customFieldTemplateId = getCustomFieldTemplateId(field.id);
      const fieldValue =
        (field.initialValue ? field.initialValue : parseValue(field.inputType, ""))?.toString() ??
        "";

      customFields.push(customFieldDataFor(entity, customFieldTemplateId, fieldValue));

      localStateUpdates[`customField${customFieldTemplateId}`] = fieldValue;
    } else {
      regularFields[fieldAttrName(field)] = field.initialValue
        ? field.initialValue
        : parseValue(field.inputType, "");
    }
  });

  return { customFields, regularFields, localStateUpdates };
};

export const createBulkOperationPayload = (fields, entity) => {
  const { customFields, regularFields, localStateUpdates } = processFieldsForBulkOperation(
    fields,
    entity
  );

  const payload = { ...regularFields };
  if (customFields.length > 0) {
    payload.customFields = customFields;
  }

  const localStateUpdate = { ...regularFields, ...localStateUpdates };
  if (customFields.length > 0) {
    localStateUpdate.customFields = withCustomFieldValues(entity, customFields);
  }

  return { payload, localStateUpdate };
};

export const buildFillDownPayload = (fillDownDiapason, entities, fields) => {
  const rootCoords = fillDownDiapason[0];
  const endCoords = fillDownDiapason[1];
  const copiedRecord = entities[rootCoords[0]];

  const startRow = Math.min(rootCoords[0], endCoords[0]);
  const endRow = Math.max(rootCoords[0], endCoords[0]);
  const startCol = Math.min(rootCoords[1], endCoords[1]);
  const endCol = Math.max(rootCoords[1], endCoords[1]);

  const pasteFields = fields
    .slice(startCol, endCol + 1)
    .filter(field => field.visible && !field.inactive && !field.unEditable);

  const variables = {};
  const localStateVariables = {};

  for (let rowIndex = startRow; rowIndex <= endRow; rowIndex++) {
    if (rowIndex === rootCoords[0]) continue;

    const targetRecord = entities[rowIndex];
    if (!targetRecord) continue;

    const { payload, localStateUpdate } = buildRecordPayloadForFillDown(
      copiedRecord,
      targetRecord,
      pasteFields
    );

    variables[targetRecord.id] = payload;
    localStateVariables[targetRecord.id] = localStateUpdate;
  }

  return { variables, localStateVariables };
};

const buildRecordPayloadForFillDown = (sourceRecord, targetRecord, fields) => {
  const recordPayload = {};
  const customFieldsArray = [];
  const localStateUpdates = {};

  fields.forEach(field => {
    const sourceValue = callerField(sourceRecord, field);

    if (isCustomField(field)) {
      const customFieldTemplateId = getCustomFieldTemplateId(field.id);
      customFieldsArray.push(customFieldDataFor(targetRecord, customFieldTemplateId, sourceValue));
      localStateUpdates[`customField${customFieldTemplateId}`] = sourceValue?.toString() ?? "";
    } else if (isCostCenterField(field)) {
      const fieldKey = fieldAttrName(field);
      const costCenterId = getCostCenterId(sourceRecord);
      recordPayload[fieldKey] = costCenterId;
      localStateUpdates[fieldKey] = costCenterId;
      localStateUpdates.costCenter = sourceRecord.costCenter;
    } else {
      const fieldKey = fieldAttrName(field);
      recordPayload[fieldKey] = sourceValue;
      localStateUpdates[fieldKey] = sourceValue;
    }
  });

  if (customFieldsArray.length > 0) {
    recordPayload.customFields = customFieldsArray;
    localStateUpdates.customFields = withCustomFieldValues(targetRecord, customFieldsArray);
  }

  return { payload: recordPayload, localStateUpdate: localStateUpdates };
};
